import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PaymentSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'success' | 'failed' | 'pending'>('loading');
  const [paymentData, setPaymentData] = useState<any>(null);

  const reference = searchParams.get('reference');

  useEffect(() => {
    if (!reference) {
      setPaymentStatus('failed');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('mobile_money_payments')
          .select('*')
          .eq('reference', reference)
          .single();

        if (error) {
          console.error('Error fetching payment:', error);
          setPaymentStatus('failed');
          return;
        }

        setPaymentData(data);

        if (data.status === 'completed') {
          setPaymentStatus('success');
          toast({
            title: "Payment Successful!",
            description: `Your account has been topped up with TZS ${data.amount.toLocaleString()}`,
          });
        } else if (data.status === 'failed') {
          setPaymentStatus('failed');
        } else {
          setPaymentStatus('pending');
          // Continue checking for updates
          setTimeout(checkPaymentStatus, 3000);
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        setPaymentStatus('failed');
      }
    };

    checkPaymentStatus();
  }, [reference, toast]);

  const handleGoToDashboard = () => {
    navigate('/user/dashboard');
  };

  const handleGoToTopUp = () => {
    navigate('/user/topup');
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'loading':
        return (
          <div className="text-center">
            <Loader2 size={64} className="text-blue-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment...</h2>
            <p className="text-gray-600 mb-4">Please wait while we confirm your payment</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Confirmed!</h2>
            <p className="text-gray-600 mb-4">
              Your payment has been verified and your account has been topped up with TZS {paymentData?.amount?.toLocaleString()}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-semibold"
              >
                Go to Dashboard
              </button>
              <button
                onClick={handleGoToTopUp}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
              >
                Top Up Again
              </button>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <Loader2 size={64} className="text-yellow-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Pending</h2>
            <p className="text-gray-600 mb-4">
              Your payment is being processed. Please complete the M-Pesa prompt on your phone.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              This page will update automatically when your payment is confirmed.
            </p>
            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors font-semibold"
            >
              Continue to Dashboard
            </button>
          </div>
        );

      case 'failed':
        return (
          <div className="text-center">
            <AlertCircle size={64} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-4">
              {reference ? 'Unable to verify your payment. Please try again.' : 'Invalid payment reference.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleGoToTopUp}
                className="w-full py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
              >
                Try Again
              </button>
              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-semibold"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <MainLayout title="Payment Status">
      <div className="p-4 flex items-center justify-center min-h-[60vh]">
        <div className="max-w-md w-full">
          {renderContent()}
          
          {reference && (
            <div className="mt-6 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 text-center">
                Reference: {reference}
              </p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default PaymentSuccess;